import type { ReactElement } from 'react';
import { useCallback, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { DataTable } from '@/components/DataTable';
import type { DataFetchParams } from '@/components/DataTable';
import type {
  BulkToolbarContext,
  RowSelectionOptions,
} from '@/components/DataTable/types';
import type {
  AlertVisibility,
  GroupedAlert,
} from '@kirin/api-client/contracts/alerts';
import { queryKeys } from '@/hooks/api/queryKeys';
import { AlertVisibilitySelector } from '@/components/AlertVisibilitySelector';
import { useAlertVisibility } from '@/hooks/useAlertVisibility';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import { useAlertsColumnCustomization } from '@/hooks/preferences/useAlertsColumnLayout';
import { sanitizeAlertsParams } from '@/components/DataTable/alerts/sanitizeAlertsParams';
import {
  useCanViewAdminContent,
  useIsOwnerOrManager,
  useIsSupport,
  useOrgWriteGate,
} from '@/hooks/useRoleCheck';
import {
  createAlertsColumns,
  createOrganizationAlertsActionsColumn,
  createAlertStatusColumn,
  createAlertCommentColumn,
} from './columns';
import { AlertsBulkActionBar } from '@/components/AlertsBulkActions/AlertsBulkActionBar';
import { fetchOrganizationAlerts } from './api';
import { AlertDetailContent } from '@/components/grouped-alerts/AlertDetailContent';
import { AlertPopOutButton } from '@/components/grouped-alerts/AlertPopOutButton';
import { ChevronRight } from 'lucide-react';
import { ALERTS_TABLE_CONFIG } from './utils';

/**
 * Generate query keys for the DataTable.
 *
 * `visibility` is appended to the key only when it is not `'active'` so
 * toggling away from the default triggers a refetch instead of returning
 * the previously-cached default-list page. The default value is omitted
 * entirely so the cache key for the default-list baseline stays
 * byte-identical to the pre-KIR-2512 wire shape.
 */
function getQueryKeysFromParams(
  organizationId: string,
  params: DataFetchParams,
  visibility: AlertVisibility
) {
  const search = params.search;
  const baseKey = queryKeys.organizations.groupedAlerts(organizationId, {
    page: params.pagination.pageIndex + 1,
    limit: params.pagination.pageSize,
    filters: params.filters,
    sortBy: params.sort?.field,
    sortOrder: params.sort?.order,
    search,
  });
  if (visibility === 'active') return baseKey;
  return [...baseKey, { visibility }];
}

function buildOrgScopeKey(organizationId: string): string {
  return `org:${organizationId}`;
}

/**
 * DataTable-based alerts tab for organization
 */
export function DataTableAlertsContent({
  organizationId,
}: {
  organizationId: string;
}): ReactElement {
  const { flags } = useFeatureFlags([
    flagsmithFeatureFlags.ALERTS_REVAMP,
    flagsmithFeatureFlags.ENDPOINT_IDENTIFIER,
    flagsmithFeatureFlags.SECURITY_GROUPS_POLICIES,
    flagsmithFeatureFlags.ALERT_FEEDBACK,
    flagsmithFeatureFlags.PROJECTS_INVENTORY,
    flagsmithFeatureFlags.AI_RISK_MAP,
  ]);
  const revampEnabled = flags[flagsmithFeatureFlags.ALERTS_REVAMP] === true;
  const endpointsColumnEnabled =
    flags[flagsmithFeatureFlags.ENDPOINT_IDENTIFIER] ?? false;
  // KIR-3800. Org alerts only — this table is the organization view, and the
  // group column is meaningless in the personal dashboard (one user, one scope).
  const securityGroupColumnEnabled =
    flags[flagsmithFeatureFlags.SECURITY_GROUPS_POLICIES] ?? false;
  // KIR-5075: the Project column (and its filter, the Risk Map's project
  // deep-link target) ships with the AI Risk Map and needs the projects
  // inventory for its options, so both flags must be on.
  const projectColumnEnabled =
    (flags[flagsmithFeatureFlags.PROJECTS_INVENTORY] ?? false) &&
    (flags[flagsmithFeatureFlags.AI_RISK_MAP] ?? false);
  // KIR-4440: org-only status + comment feedback columns.
  const feedbackEnabled = flags[flagsmithFeatureFlags.ALERT_FEEDBACK] === true;

  // Read-gate: owners, managers, and support can all view org-wide content
  // (including the user-filterable column backed by the members list).
  const { canViewAdminContent, isLoading: canViewLoading } =
    useCanViewAdminContent(organizationId);

  // Write-gate for bulk actions. Owners/managers get working actions; support
  // gets the selection UI with the action buttons DISABLED ("disable, don't
  // hide", matching the per-row buttons); members get nothing. Distinct from
  // the read-gate above.
  const writeGate = useOrgWriteGate(organizationId);

  const scopeKey = buildOrgScopeKey(organizationId);
  const { visibility, setVisibility } = useAlertVisibility(scopeKey);

  // Show the Actions column when the viewer can see org-wide content (owner /
  // manager / support). Support sees the column with individual write buttons
  // rendered DISABLED via useOrgWriteGate — "disable, don't hide". Members
  // (canViewAdminContent false) never see the column. The "Add to
  // Allow/Block List" action (KIR-3558) lives in the expanded row's "DETECTED
  // ON CLIENT" panel, not this column, so it doesn't gate it.
  const showActionsColumn = !canViewLoading && canViewAdminContent;
  // Members get no Actions column (so no Actions header), so their pop-out
  // sits beside each row's expand caret instead.
  const showCaretPopOut = !canViewLoading && !canViewAdminContent;

  // The Users filter sources its options from the org members list, which is
  // RBAC-gated to owner/manager/support. Only offer the filter to viewers who
  // can actually load it; members get a non-filterable Users column and never
  // fire the members fetch (→ 403). KIR-3266.
  const canFilterByUser = !canViewLoading && canViewAdminContent;

  // The Security Group filter lists the org's group roster — the same data as
  // the Security Groups page, which owner/manager/support read (KIR-5086).
  const canFilterBySecurityGroup = canFilterByUser;
  const { isOwnerOrManager } = useIsOwnerOrManager(organizationId);

  // KIR-4440 feedback edit gates:
  // - Status is a verdict → editable by owner/manager only (others see a
  //   read-only badge).
  // - Comment is context anyone can add → editable by every member EXCEPT
  //   support (support is read-only per house RBAC).
  const { isSupport } = useIsSupport(organizationId);
  const canEditStatus = isOwnerOrManager;
  const canEditComment = !isSupport;

  // KIR-4089: per-user show/hide/reorder (flag-gated; undefined = fixed set).
  const columnCustomization = useAlertsColumnCustomization();

  const columns = useMemo(() => {
    const base = createAlertsColumns(
      organizationId,
      revampEnabled,
      canFilterByUser,
      endpointsColumnEnabled,
      securityGroupColumnEnabled,
      canFilterBySecurityGroup,
      projectColumnEnabled
    );
    // KIR-4440: Status then Comment sit to the right of the base columns.
    const feedbackCols = feedbackEnabled
      ? [
          createAlertStatusColumn(organizationId, canEditStatus),
          createAlertCommentColumn(organizationId, canEditComment),
        ]
      : [];
    // KIR-4700: Actions leads the column set (before "Last Seen") instead of
    // trailing it, so the row's controls are visible without scrolling.
    const actionsCols = showActionsColumn
      ? [createOrganizationAlertsActionsColumn(organizationId)]
      : [];
    return [...actionsCols, ...base, ...feedbackCols];
  }, [
    organizationId,
    showActionsColumn,
    revampEnabled,
    canFilterByUser,
    endpointsColumnEnabled,
    securityGroupColumnEnabled,
    canFilterBySecurityGroup,
    projectColumnEnabled,
    feedbackEnabled,
    canEditStatus,
    canEditComment,
  ]);

  // Bulk selection is gated by the write gate — NOT by `showActionsColumn`,
  // which is a read gate.
  //
  // `!writeGate.isLoading` is load-bearing: while the role resolves the gate
  // reports `hidden: false, disabled: false`, so dropping it would flash
  // working checkboxes and a live bulk bar at members before the role lands.
  const bulkSelectionEnabled = !writeGate.isLoading && !writeGate.hidden;
  const rowSelectionOptions = useMemo<
    RowSelectionOptions<GroupedAlert> | undefined
  >(
    () =>
      bulkSelectionEnabled
        ? {
            getRowId: (row: GroupedAlert) => row.alertKey,
            // Tabs (Active/All/Suppressed) swap the list without touching
            // filters/search, so feed visibility in as the reset token to clear
            // the selection on tab change.
            resetToken: visibility,
            // The table fills a fixed box; the bar hangs in the space below.
            hangBulkToolbar: true,
            renderBulkToolbar: (ctx: BulkToolbarContext<GroupedAlert>) => (
              <AlertsBulkActionBar
                ctx={ctx}
                scope={{ kind: 'organization', organizationId }}
                visibility={visibility}
                disabled={writeGate.disabled}
              />
            ),
          }
        : undefined,
    [bulkSelectionEnabled, organizationId, visibility, writeGate.disabled]
  );

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const alertsBasePath = `/${organizationId}`;
  const searchInputProps = {
    'aria-label': 'Search alerts',
  };
  const searchConfig = {
    placeholder: 'Search alerts',
    className: 'w-[260px]',
    inputProps: searchInputProps,
  };

  const sanitizeParams = useCallback(
    (params: DataFetchParams): DataFetchParams =>
      sanitizeAlertsParams(params, canFilterByUser, endpointsColumnEnabled),
    [canFilterByUser, endpointsColumnEnabled]
  );

  const queryFn = useCallback(
    (params: DataFetchParams, signal?: AbortSignal) =>
      fetchOrganizationAlerts(
        organizationId,
        sanitizeParams(params),
        signal,
        visibility === 'active' ? undefined : visibility
      ),
    [organizationId, visibility, sanitizeParams]
  );

  const queryKeyFn = useCallback(
    (params: DataFetchParams) =>
      getQueryKeysFromParams(
        organizationId,
        sanitizeParams(params),
        visibility
      ),
    [organizationId, visibility, sanitizeParams]
  );

  // Visibility affects total count — include it in the knownTotal fingerprint
  // so toggling visibility invalidates the cached total.
  const countScope = useMemo(() => ({ visibility: visibility }), [visibility]);

  // Dim a row when ANY suppression state is set. The two states are
  // orthogonal at the data layer (a row can be both muted and excluded), so
  // OR is the right combinator from the user's perspective: "this row is
  // currently suppressed somehow".
  //
  // The dim is scoped to every cell EXCEPT the Actions cell via the
  // `[&>td:not([data-column-id='actions'])]` arbitrary variant rather than
  // applied at row-level. Reason: CSS opacity creates a stacking context and
  // cascades to ALL descendants regardless of any child opacity override, so a
  // row-level `opacity-60` would also dim the Mute/Unmute/Create-Exclusion/
  // Remove-Exclusion buttons in the Actions cell — which users read as "the
  // action is unavailable" (it isn't; it's the primary affordance for
  // un-suppressing the row). Targeting the Actions cell by its stable
  // `data-column-id` (set by `TableBody`) — not `:last-child` — keeps this
  // correct regardless of column order (KIR-4700 moved Actions to lead the
  // column set, ahead of Last Seen) or whether the Actions column is present
  // at all.
  const getRowClassName = useCallback(
    (row: GroupedAlert): string | undefined =>
      row.mute?.muted || row.exclude?.excluded
        ? "[&>td:not([data-column-id='actions'])]:opacity-60"
        : undefined,
    []
  );

  // KIR-2748: keep the table inside ONE viewport-constrained flex column so
  // it never pushes the page column past 100vh (the outer-scroll bug). The
  // box does not clip: the bulk bar hangs just below it, in the room above
  // the "Contact us" footer (KIR-5379). The DataTable flexes to fill the space (`flex-1
  // min-h-0`) and keeps its own inner overflow scroll, so any table overflow
  // stays inside the table, never the page. The visibility selector rides in
  // the table toolbar rather than a row of its own, so the table starts higher.
  return (
    <div className="flex flex-col h-[calc(100vh-11rem)]">
      <DataTable<GroupedAlert>
        id={ALERTS_TABLE_CONFIG.tableId}
        mode="server"
        className="flex flex-col flex-1 min-h-0"
        containerClassName="flex-1"
        columns={columns}
        columnCustomization={columnCustomization}
        errorBoundaryLabel="Something went wrong in the Alerts table"
        search={searchConfig}
        toolbarEndSlot={
          <AlertVisibilitySelector
            scopeKey={scopeKey}
            value={visibility}
            onChange={setVisibility}
          />
        }
        features={{ enableRowExpansion: true, enableSorting: true }}
        rowExpansion={{
          icon: <ChevronRight className="h-4 w-4" />,
          ...(showCaretPopOut
            ? {
                toggleAddon: (row: GroupedAlert) => (
                  <AlertPopOutButton
                    alertKey={row.alertKey}
                    organizationId={organizationId}
                  />
                ),
              }
            : {}),
          getRowId: row => row.alertKey,
          render: row => {
            const occurrencesPath = `${alertsBasePath}/alerts/${encodeURIComponent(row.alertKey)}/occurrences`;
            const occurrencesUrl = searchParams.toString()
              ? `${occurrencesPath}?${searchParams.toString()}`
              : occurrencesPath;

            return (
              <div className="min-w-0 max-w-full">
                <AlertDetailContent
                  alert={row}
                  organizationId={organizationId}
                  scope={{ kind: 'organization', organizationId }}
                  alertKey={row.alertKey}
                  onShowOccurrences={() => navigate(occurrencesUrl)}
                />
              </div>
            );
          },
          contentClassName: 'bg-muted/30 p-4',
          rowClassName: 'bg-muted/10',
          keepMounted: false,
          showContainer: false,
        }}
        queryFn={queryFn}
        getQueryKeysFromParams={queryKeyFn}
        countScope={countScope}
        rowSelection={rowSelectionOptions}
        getRowClassName={getRowClassName}
        noDataLabel="No alerts found."
        queryConfig={{
          staleTime: 30000,
          refetchOnWindowFocus: false,
        }}
      />
    </div>
  );
}

DataTableAlertsContent.displayName = 'DataTableAlertsContent';

export default function DataTableAlertsTab(): ReactElement | null {
  const { organizationId } = useParams<{ organizationId: string }>();

  if (!organizationId) {
    return null;
  }

  return (
    <DataTableAlertsContent
      key={organizationId}
      organizationId={organizationId}
    />
  );
}
